import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
  type AxiosRequestConfig,
} from "axios";
import { useUserStore } from "@/store/UserStore";
import { authService } from "./AuthService";
import { getDadaToken } from "./dadaToken";
import ToastEvent from "./toast-event";

export function getApiHeaders(
  contentType = "application/json",
): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": contentType,
  };

  const token = useUserStore.getState().token;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const dadaToken = getDadaToken();
  if (dadaToken) {
    headers["Dada-Token"] = dadaToken;
  }

  return headers;
}

const service: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "",
  timeout: 15000, // 超时时间15秒
});

// 请求拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useUserStore.getState().token;

    config.headers = config.headers || {};

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    const dadaToken = getDadaToken();
    if (dadaToken) {
      config.headers["Dada-Token"] = dadaToken;
    }

    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  },
);

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const res = response.data;
    // 根据后端返回格式，目前暂时预设 status 为 0 表示成功
    if (res.status !== 0) {
      console.error("Response error:", res.msg || "Unknown error");

      if (res.status === 401) {
        authService.triggerLogout();
        ToastEvent.error("会话已过期，请重新登录");
      } else if (res.status === 500) {
        ToastEvent.error("后台繁忙，请稍后再试");
      } else {
        ToastEvent.error(res.msg || "Unknown error");
      }
      return Promise.reject(new Error(res.msg || "Unknown error"));
    }
    return res;
  },
  (error) => {
    console.error("Response error:", error.message);
    if (error.response && error.response.status === 401) {
      authService.triggerLogout();
      ToastEvent.error("会话已过期，请重新登录");
    } else {
      ToastEvent.error(error.message || "网络请求失败");
    }
    return Promise.reject(error);
  },
);

export const request = {
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return service.get(url, config);
  },

  post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return service.post(url, data, config);
  },

  put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return service.put(url, data, config);
  },

  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return service.delete(url, config);
  },

  patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return service.patch(url, data, config);
  },
};

export default service;
